import React, {Fragment, useEffect, useMemo, useState} from 'react';
import {Dialog, Transition} from "@headlessui/react";
import {ICategory, ICategoryUsage} from "../../model/ICategory";
import {TransactionType} from "../../model/TransactionType";
import {categoryApi} from "../../api/CategoriesService";
import CategoryPicker from "../ui/transaction/CategoryPicker";
import {getApiErrorMessage} from "./categoryTreeUtils";

interface DeleteCategoryDialogProps {
    open: boolean;
    trType: TransactionType;
    category: ICategory | null;
    usage: ICategoryUsage | null;
    categories: ICategory[];
    onClose: () => void;
    onDeleted: () => void;
}

const DeleteCategoryDialog: React.FunctionComponent<DeleteCategoryDialogProps> = ({
    open,
    trType,
    category,
    usage,
    categories,
    onClose,
    onDeleted,
}) => {
    const [replacement, setReplacement] = useState<ICategory | null>(null);
    const [error, setError] = useState('');
    const [deleteCategory, {isLoading}] = categoryApi.useDeleteCategoryMutation();

    const replacementOptions = useMemo(
        () => categories.filter((option) => option.id !== category?.id),
        [categories, category],
    );

    const needsReplacement = Boolean(
        usage && (usage.transaction_count > 0 || usage.bulk_upload_row_count > 0),
    );

    useEffect(() => {
        if (open) {
            setReplacement(null);
            setError('');
        }
    }, [open, category]);

    function handleClose() {
        setReplacement(null);
        setError('');
        onClose();
    }

    async function handleDelete() {
        if (!category) {
            return;
        }
        if (needsReplacement && !replacement) {
            setError('Select a replacement category.');
            return;
        }
        setError('');
        try {
            await deleteCategory({
                trType,
                id: category.id,
                replacement: replacement?.id ?? null,
            }).unwrap();
            handleClose();
            onDeleted();
        } catch (err) {
            setError(getApiErrorMessage(err, 'Could not delete category.'));
        }
    }

    const usageSummary = usage
        ? [
            usage.transaction_count > 0 ? `${usage.transaction_count} transaction${usage.transaction_count === 1 ? '' : 's'}` : '',
            usage.bulk_upload_row_count > 0
                ? `${usage.bulk_upload_row_count} bulk upload row${usage.bulk_upload_row_count === 1 ? '' : 's'}`
                : '',
        ].filter(Boolean).join(' and ')
        : '';

    return (
        <Transition show={open} as={Fragment}>
            <Dialog as="div" className="relative z-[200]" onClose={handleClose}>
                <Transition.Child
                    as={Fragment}
                    enter="ease-out duration-200"
                    enterFrom="opacity-0"
                    enterTo="opacity-100"
                    leave="ease-in duration-150"
                    leaveFrom="opacity-100"
                    leaveTo="opacity-0"
                >
                    <div className="fixed inset-0 bg-black/30"/>
                </Transition.Child>

                <div className="fixed inset-0 overflow-y-auto">
                    <div className="flex min-h-full items-center justify-center p-4">
                        <Transition.Child
                            as={Fragment}
                            enter="ease-out duration-200"
                            enterFrom="opacity-0 scale-95"
                            enterTo="opacity-100 scale-100"
                            leave="ease-in duration-150"
                            leaveFrom="opacity-100 scale-100"
                            leaveTo="opacity-0 scale-95"
                        >
                            <Dialog.Panel className="w-full max-w-md transform overflow-visible rounded-md bg-white p-6 shadow-xl">
                                <Dialog.Title className="text-lg font-medium text-gray-900">
                                    Delete category
                                </Dialog.Title>
                                <p className="mt-2 text-sm text-gray-600">
                                    {needsReplacement
                                        ? `${usageSummary} use “${category?.name}”. Choose another category to move them to.`
                                        : `Delete “${category?.name}”? This cannot be undone.`}
                                </p>
                                {needsReplacement && (
                                    <div className="mt-4">
                                        <CategoryPicker
                                            label="Replacement"
                                            category={replacement}
                                            setCategory={setReplacement}
                                            categories={replacementOptions}
                                            className="w-full"
                                        />
                                    </div>
                                )}
                                {error && <p className="mt-3 text-sm text-red-700">{error}</p>}
                                <div className="mt-6 flex justify-end gap-2">
                                    <button
                                        type="button"
                                        className="py-1 px-3 text-xs font-medium rounded-md bg-gray-200 text-red-500 hover:text-red-800"
                                        onClick={handleClose}
                                    >
                                        Cancel
                                    </button>
                                    <button
                                        type="button"
                                        className="py-1 px-3 text-xs font-medium rounded-md bg-red-100 text-red-800 hover:bg-red-200"
                                        disabled={isLoading || (needsReplacement && !replacement)}
                                        onClick={handleDelete}
                                    >
                                        Delete
                                    </button>
                                </div>
                            </Dialog.Panel>
                        </Transition.Child>
                    </div>
                </div>
            </Dialog>
        </Transition>
    );
};

export default DeleteCategoryDialog;
